/**
 * InvestmentDetailPage Component
 *
 * Read-only view page for investment details
 * Displays all investment information in a view-only format
 */

import {
  useInvestment,
  useCosts,
  useBenefits,
  usePortcos,
  useSegments,
  useResources,
  useInvestmentScores,
  useRoadmapItems,
} from '@medsuite/api-client';
import {
  StatusBadge,
  Button,
  Card,
  Spinner,
  Alert,
  Tabs,
  TabList,
  TabItem,
  TabPanels,
  TabPanel,
} from '@medsuite/ui-components';
import { CostTable, BenefitTable } from '@medsuite/ui-tables';
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import ScoringTab from './components/StrategySelector';
import { CommentsTab } from './tabs/CommentsTab';
import { DecisionsTab } from './tabs/DecisionsTab';
import { SummarySubTab } from './tabs/financials/SummarySubTab';
import { RelatedTab } from './tabs/RelatedTab';
import { RisksViewTab } from './tabs/RisksViewTab';
import { SummaryTab } from './tabs/SummaryTab';

export const InvestmentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const investmentId = parseInt(id || '0', 10);
  const [activeTab, setActiveTab] = useState(0);
  const [financialSubTab, setFinancialSubTab] = useState(0);

  // Fetch investment data
  const {
    data: investment,
    isLoading,
    isError,
    error,
  } = useInvestment(investmentId, {
    enabled: !isNaN(investmentId) && investmentId > 0,
  });

  // Fetch related data
  const { data: costs = [] } = useCosts(investmentId);
  const { data: benefits = [] } = useBenefits(investmentId);
  const { data: portcos = [] } = usePortcos();
  const { data: segments = [] } = useSegments();
  const { data: resources = [] } = useResources(investmentId);
  const { data: scoresData } = useInvestmentScores(investmentId);

  // Check if this investment is on a roadmap
  const { data: roadmapItems = [] } = useRoadmapItems(
    { investment_id: investmentId },
    { enabled: !isNaN(investmentId) && investmentId > 0 }
  );
  const roadmapItem = roadmapItems[0]; // Get the first roadmap item if exists

  // Filter resources by type
  const internalResources = resources.filter((r) => r.resource_type === 'I');
  const externalResources = resources.filter((r) => r.resource_type === 'E');
  const otherResources = resources.filter((r) => r.resource_type === 'O');

  // Format date helper
  const formatResourceDate = (date: Date | string | null | undefined) => {
    if (!date) {
      return '-';
    }
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get lookup names
  const portcoName = portcos.find((p) => p.portco_id === investment?.portco_id)?.portco_name || '';
  const segmentName =
    segments.find((s) => s.segment_id === investment?.segment_id)?.segment_name || '';
  const submittedDate = investment?.submitted_date
    ? new Date(investment.submitted_date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '';

  // Get composite score display
  const hasScoring =
    scoresData?.composite_score?.strategy_count && scoresData.composite_score.strategy_count > 0;
  const compositeScore = hasScoring ? Math.round(scoresData.composite_score.total_score) : null;

  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading investment...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (isError || !investment) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert variant="error">
          <p className="font-semibold">Failed to load investment</p>
          <p className="text-sm">
            {error?.message || 'Investment not found or could not be loaded.'}
          </p>
        </Alert>
        <div className="mt-4">
          <Button onClick={() => navigate('/')}>Back to Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-6 py-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {investment.title || 'Untitled Investment'}
              </h1>
              <StatusBadge status={investment.status} />
              {(compositeScore === null || compositeScore === 0) && (
                <span className="inline-flex items-center gap-1.5 rounded-full font-medium px-2.5 py-0.5 text-sm bg-red-100 dark:bg-red-950 text-red-800 dark:text-red-200">
                  <span
                    className="rounded-full h-2 w-2 bg-red-400 dark:bg-red-500"
                    aria-hidden="true"
                  />
                  Missing Score
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {compositeScore !== null && compositeScore > 0 && (
                <>
                  <span className="font-bold">Score: {compositeScore}</span>
                  {(portcoName || segmentName || submittedDate) && ' - '}
                </>
              )}
              {portcoName}
              {portcoName && segmentName ? ' - ' : ''}
              {segmentName}
              {(portcoName || segmentName) && submittedDate ? ' - ' : ''}
              {submittedDate && `Submitted ${submittedDate}`}
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/')}>
              Back to List
            </Button>
            {roadmapItem && (
              <Button
                variant="outline"
                onClick={() => navigate(`/roadmap/${roadmapItem.roadmap_id}`)}
              >
                Show on Roadmap
              </Button>
            )}
            <Button onClick={() => navigate(`/investments/${investmentId}/edit`)}>
              Edit Investment
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto h-full px-6 py-6">
          <Tabs selectedIndex={activeTab} onChange={setActiveTab}>
            <TabList>
              <TabItem>Summary</TabItem>
              <TabItem>Resources</TabItem>
              <TabItem>Financials</TabItem>
              <TabItem>Scoring</TabItem>
              <TabItem>Risks</TabItem>
              <TabItem>Comments</TabItem>
              <TabItem>Decisions</TabItem>
              <TabItem>Related</TabItem>
            </TabList>

            <TabPanels className="h-[calc(100vh-250px)] overflow-y-auto">
              {/* Tab 1: Summary */}
              <TabPanel>
                <SummaryTab investment={investment} />
              </TabPanel>

              {/* Tab 2: Resources */}
              <TabPanel>
                <div className="space-y-6">
                  {/* Internal Labor Resources */}
                  <Card>
                    <div className="p-6">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                        Internal Labor Resources
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        Internal employees allocated to this investment
                      </p>
                      {internalResources.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                          No internal resources defined
                        </p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-800">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Resource Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Project Role
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Start Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  End Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Allocation %
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                              {internalResources.map((resource) => (
                                <tr
                                  key={resource.investment_resource_id}
                                  className="hover:bg-gray-50 dark:hover:bg-gray-800"
                                >
                                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                                    {resource.resource_name || '-'}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                                    {resource.project_role || '-'}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                                    {formatResourceDate(resource.resource_start_date)}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                                    {formatResourceDate(resource.resource_end_date)}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                                    {resource.resource_allocation != null
                                      ? `${resource.resource_allocation}%`
                                      : '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </Card>

                  {/* External Labor Resources */}
                  <Card>
                    <div className="p-6">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                        External Labor Resources
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        Contractors and external staff allocated to this investment
                      </p>
                      {externalResources.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                          No external resources defined
                        </p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-800">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Resource Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Project Role
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Start Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  End Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Allocation %
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                              {externalResources.map((resource) => (
                                <tr
                                  key={resource.investment_resource_id}
                                  className="hover:bg-gray-50 dark:hover:bg-gray-800"
                                >
                                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                                    {resource.resource_name || '-'}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                                    {resource.project_role || '-'}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                                    {formatResourceDate(resource.resource_start_date)}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                                    {formatResourceDate(resource.resource_end_date)}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                                    {resource.resource_allocation != null
                                      ? `${resource.resource_allocation}%`
                                      : '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </Card>

                  {/* Other Resources */}
                  <Card>
                    <div className="p-6">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                        Other Resources (Systems, Licenses, Tools)
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        Hardware, software, licenses, and other non-labor resources
                      </p>
                      {otherResources.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                          No other resources defined
                        </p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-800">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Resource Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Quantifier
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Temporary or Perpetual
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                              {otherResources.map((resource) => (
                                <tr
                                  key={resource.investment_resource_id}
                                  className="hover:bg-gray-50 dark:hover:bg-gray-800"
                                >
                                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                                    {resource.resource_name || '-'}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                                    {resource.quantifier || '-'}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                                    {resource.temporary_or_perpetual === 'T'
                                      ? 'Temporary'
                                      : resource.temporary_or_perpetual === 'P'
                                        ? 'Perpetual'
                                        : '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </Card>
                </div>
              </TabPanel>

              {/* Tab 3: Financials */}
              <TabPanel>
                <Tabs selectedIndex={financialSubTab} onChange={setFinancialSubTab}>
                  <TabList>
                    <TabItem>Summary</TabItem>
                    <TabItem>Costs & Benefits</TabItem>
                  </TabList>

                  <TabPanels className="mt-4">
                    {/* Sub-tab 1: Summary */}
                    <TabPanel>
                      <SummarySubTab investment={investment} />
                    </TabPanel>

                    {/* Sub-tab 2: Costs & Benefits */}
                    <TabPanel>
                      <div className="space-y-8">
                        {/* Costs Section */}
                        <Card>
                          <div className="p-6">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                              Investment Costs
                            </h3>
                            <CostTable costs={costs} readOnly={true} showTotals={true} />
                          </div>
                        </Card>

                        {/* Benefits Section */}
                        <Card>
                          <div className="p-6">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                              Investment Benefits
                            </h3>
                            <BenefitTable benefits={benefits} readOnly={true} showTotals={true} />
                          </div>
                        </Card>
                      </div>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </TabPanel>

              {/* Tab 4: Scoring */}
              <TabPanel>
                <Card>
                  <div className="p-6">
                    <ScoringTab investment={investment} />
                  </div>
                </Card>
              </TabPanel>

              {/* Tab 5: Risks */}
              <TabPanel>
                <RisksViewTab investment={investment} />
              </TabPanel>

              {/* Tab 6: Comments */}
              <TabPanel>
                <CommentsTab investment={investment} />
              </TabPanel>

              {/* Tab 7: Decisions */}
              <TabPanel>
                <DecisionsTab investment={investment} />
              </TabPanel>

              {/* Tab 8: Related (Attachments + Links) */}
              <TabPanel>
                <RelatedTab investment={investment} />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default InvestmentDetailPage;
